import { message, Table } from 'antd'
import PrintQueueTableWrapper from '../../../../../../../components/print-queue-table/print-queue-table-wrapper'
import { getPrintQueueEntryRowClassName } from '../../../../../../machines-schedule/components/one-machine-schedule/helpers/print-job-helpers'
import { FC, useEffect, useMemo, useState } from 'react'
import { getColumns, insertEndTimeColumn } from '../../columns'
import { buildPrintQueueEndTimes } from '../../../../helpers/build-print-queue-end-times'
import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import CustomRow from '../custom-row'
import {
    useGetPrintQueueEntriesQuery,
    useReorderPrintQueueEntriesMutation,
} from '../../../../../../../store/api/print-queue-entries-api'
import { DocumentId } from '../../../../../../../types/strapi/strapi'
import useQueryParams from './hooks/use-query-params'
import { TableRowSelection } from 'antd/es/table/interface'
import PrintQueueEntry from '../../../../../../../types/api/print-queue-entry'
import RowSelectionColumnTitle from './components/row-selection-column-title'
import { useHasRole } from '../../../../../../../hooks/use-has-role'
import { PRINT_PLAN_EDIT_ROLES } from '../../../../constants/print-plan-edit-roles'
import styled from 'styled-components'
import { buildScopedReorderPayload, mergeScopedEntriesIntoMachineQueue } from '../../../../helpers/move-print-queue-entries-to-day'

const TableWrapperStyled = styled(PrintQueueTableWrapper)`
    flex: auto;
    height: 100%;
    width: 100%;
`

interface MachineQueueProps {
    machineDocId?: DocumentId
    entries?: PrintQueueEntry[]
    allEntriesForReorder?: PrintQueueEntry[]
    executionOrderConflicts?: Map<string, string>
}
// TODO: question - могу сделать чтобы постепенно закрашивалась строка исходя из времени принятия задания, нормы и текущего времени
// TODO: question - могу сделать вывод сообщения "печать на машине Н должна закончится через 10 минут"
// TODO: question - надо сделать столбец пометки, что есть связанное задание на другом станке?
const MachineQueue: FC<MachineQueueProps> = ({
    machineDocId,
    entries,
    allEntriesForReorder,
    executionOrderConflicts,
}) => {
    const queryParams = useQueryParams(machineDocId || '')
    const { data } = useGetPrintQueueEntriesQuery(queryParams, {
        skip: Boolean(entries) || !machineDocId,
    })
    const [dataSource, setDataSource] = useState(entries || data?.data || [])
    const [reorder] = useReorderPrintQueueEntriesMutation()
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const selectedPrintQueryEntries = dataSource.filter(x => selectedRowKeys.includes(x.id))
    const canManagePrintPlan = useHasRole(PRINT_PLAN_EDIT_ROLES)
    const columns = useMemo(
        () => getColumns(canManagePrintPlan, executionOrderConflicts),
        [canManagePrintPlan, executionOrderConflicts]
    )

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys)
    }

    const rowSelection: TableRowSelection<PrintQueueEntry> = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnTitle: <RowSelectionColumnTitle selected={selectedPrintQueryEntries} />,
    }

    useEffect(() => {
        setDataSource(entries || data?.data || [])
    }, [data?.data, entries, machineDocId])

    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (active.id !== over?.id) {
            setDataSource(prevState => {
                const activeIndex = prevState.findIndex(record => record.id === active?.id)
                const overIndex = prevState.findIndex(record => record.id === over?.id)
                const newOrder = arrayMove(prevState, activeIndex, overIndex).map((entry, index) => ({
                    ...entry,
                    sort: index,
                }))
                const reorderPayload = allEntriesForReorder?.length
                    ? buildScopedReorderPayload({
                          machineEntries: allEntriesForReorder,
                          scopedEntries: newOrder,
                      })
                    : newOrder.map((x, i) => ({ documentId: x.documentId, sort: i }))

                reorder({
                    data: reorderPayload,
                })
                    .unwrap()
                    .catch(() => {
                        setDataSource(prevState)
                        message.error('Не удалось сохранить порядок, попробуйте ещё раз')
                    })

                return newOrder
            })
        }
    }

    const filteredColumns = columns.filter(col => {
        if (col && 'key' in col && col.key === 'sort' && !canManagePrintPlan) {
            return false
        }
        if (col && 'dataIndex' in col && col.dataIndex === 'actions' && !canManagePrintPlan) {
            return false
        }
        return true
    })

    const entriesForEndTimes = useMemo(() => {
        if (allEntriesForReorder?.length) {
            return mergeScopedEntriesIntoMachineQueue(allEntriesForReorder, dataSource)
        }

        return [...dataSource]
    }, [allEntriesForReorder, dataSource])
    const endTimes = useMemo(
        () => buildPrintQueueEndTimes(entriesForEndTimes),
        [entriesForEndTimes]
    )
    const tableColumns = useMemo(
        () => insertEndTimeColumn(filteredColumns, endTimes),
        [filteredColumns, endTimes]
    )

    const table = (
        <TableWrapperStyled filtersSummaryHeight={0} tableHasNotPagination>
            <Table
                dataSource={dataSource}
                columns={tableColumns}
                pagination={false}
                rowKey={'id'}
                size={'small'}
                components={canManagePrintPlan ? { body: { row: CustomRow } } : undefined}
                rowClassName={getPrintQueueEntryRowClassName}
                rowSelection={canManagePrintPlan ? rowSelection : undefined}
            />
        </TableWrapperStyled>
    )

    if (!canManagePrintPlan) {
        return table
    }

    return (
        <>
            <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
                <SortableContext
                    items={dataSource.map(i => i.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {table}
                </SortableContext>
            </DndContext>
        </>
    )
}

export default MachineQueue
